import { useEffect, useContext, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLatest } from 'react-use';

import { MapContext } from '../../../contexts/MapContext';
import { symmetricalDiff } from '../../../utils/utils';
import {
  HIDDEN_LAYER_QUERY_PARAM,
  HIDDEN_SECTION_QUERY_PARAM,
  BASE_LAYER_QUERY_PARAM,
  BASE_LAYER_ROADS_QUERY_PARAM,
  BASE_LAYER_CONTOURS_QUERY_PARAM,
  BASE_LAYER_DARK_QUERY_PARAM,
} from '../constants';

// Returns array of layers ensuring that each item has an `id` key.
const useLayersWithIds = () => {
  const {
    layers: { sortedLayers },
  } = useContext(MapContext);

  return useMemo(() => sortedLayers.map((l) => ({ ...l, id: l.layerId })), [
    sortedLayers,
  ]);
};

/**
 * Sets the visibility of the items in `data` based on the search param
 * `queryParam`, using `toggleFn` to change state.
 */
const useSetVisibilityFromSearchParams = (data, queryParam, toggleFn) => {
  const [searchParams] = useSearchParams();

  const dataRef = useLatest(data);
  const toggleFnRef = useLatest(toggleFn);

  // when search params change, update state
  useEffect(() => {
    const currentHiddenIndexes = [];
    dataRef.current.forEach(({ shown }, i) => {
      if (!shown) currentHiddenIndexes.push(i);
    });

    const searchParamsHiddenIndexes = searchParams
      .getAll(queryParam)
      .map((s) => parseInt(s, 10));

    const indexesToToggle = symmetricalDiff(
      searchParamsHiddenIndexes,
      currentHiddenIndexes,
    );
    const idsToToggle = indexesToToggle
      .map((i) => dataRef.current[i]?.id)
      .filter(Boolean);

    idsToToggle.forEach((id) => {
      toggleFnRef.current(id);
    });
  }, [
    data.length, // NOTE: we use the length as a way to ensure we re-run this once the data has fully loaded.
    dataRef,
    searchParams,
    queryParam,
    toggleFnRef,
  ]);
};

/**
 * Set base map state based on the search params.
 */
const useSetBaseMapFromSearchParams = () => {
  const [searchParams] = useSearchParams();
  const {
    baseMapState: { setBaseLayerState },
  } = useContext(MapContext);

  useEffect(() => {
    const baseLayer = searchParams.get(BASE_LAYER_QUERY_PARAM);
    const newState = {
      contour: searchParams.has(BASE_LAYER_CONTOURS_QUERY_PARAM),
      roads: searchParams.has(BASE_LAYER_ROADS_QUERY_PARAM),
      dark: searchParams.has(BASE_LAYER_DARK_QUERY_PARAM),
      ...(baseLayer ? { baseLayer } : {}),
    };
    setBaseLayerState(newState);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
};

/**
 * Set layer and section state based on search params.
 */
export const useLayerAndSectionStateFromSearchParams = () => {
  const {
    layers: { toggleLayer },
    fields: { fieldSections, toggleSectionShown },
  } = useContext(MapContext);

  const layers = useLayersWithIds();

  useSetVisibilityFromSearchParams(
    layers,
    HIDDEN_LAYER_QUERY_PARAM,
    toggleLayer,
  );

  useSetVisibilityFromSearchParams(
    fieldSections,
    HIDDEN_SECTION_QUERY_PARAM,
    toggleSectionShown,
  );

  useSetBaseMapFromSearchParams();
};

/**
 * Returns a function to toggle the visibility of a layer or section, using search params.
 *
 * NOTE: data must be an array of objects with an `id` property.
 */
const useToggleVisibilityViaSearchParams = (data, queryParam) => {
  const [, setSearchParams] = useSearchParams();

  const dataRef = useLatest(data);

  return useCallback(
    (id) => {
      const index = dataRef.current.findIndex((d) => d.id === id);

      setSearchParams(
        (params) => {
          const newParams = new URLSearchParams(params);

          if (newParams.has(queryParam, index)) {
            newParams.delete(queryParam, index);
          } else {
            newParams.append(queryParam, index);
          }

          return newParams;
        },
        { replace: true },
      );
    },
    [setSearchParams, queryParam, dataRef],
  );
};

/**
 * Toggles layer visibility via search params.
 */
export const useToggleLayerVisibility = () => {
  const layers = useLayersWithIds();
  return useToggleVisibilityViaSearchParams(layers, HIDDEN_LAYER_QUERY_PARAM);
};

/**
 * Toggles section visibility via search params.
 */
export const useToggleSectionVisibility = () => {
  const {
    fields: { fieldSections },
  } = useContext(MapContext);
  return useToggleVisibilityViaSearchParams(
    fieldSections,
    HIDDEN_SECTION_QUERY_PARAM,
  );
};

export const useResetItemVisibility = () => {
  const [, setSearchParams] = useSearchParams();

  return useCallback(
    () =>
      setSearchParams((params) => {
        const newParams = new URLSearchParams(params);

        newParams.delete(HIDDEN_LAYER_QUERY_PARAM);
        newParams.delete(HIDDEN_SECTION_QUERY_PARAM);

        return newParams;
      }),
    [setSearchParams],
  );
};
